/**
 * AppearanceSettings.tsx — NMW-246 theme feature.
 *
 * Lets the operator pick the console theme (Claro / Oscuro / Sistema).
 * Wires the existing SegmentedControl primitive to the ThemeProvider
 * contract at ../../lib/theme:
 *   - choice   'light' | 'dark' | 'system'  (persisted to localStorage)
 *   - resolved 'light' | 'dark'             (the actually-applied theme)
 *
 * 'Sistema' follows the OS `prefers-color-scheme` and updates live (no
 * reload) — the ThemeProvider subscribes to the matchMedia change event.
 *
 * Spanish operator UI. Colors use only var(--…) tokens so the panel
 * stays legible in BOTH themes.
 */
import { useTheme } from '../../lib/theme';
import { Panel, SegmentedControl } from '../../components/primitives';

const OPTIONS = [
  { value: 'light', label: 'Claro' },
  { value: 'dark', label: 'Oscuro' },
  { value: 'system', label: 'Sistema' },
];

const RESOLVED_LABEL: Record<string, string> = {
  light: 'Claro',
  dark: 'Oscuro',
};

export default function AppearanceSettings() {
  const { choice, resolved, setChoice } = useTheme();

  return (
    <Panel title="Apariencia" pad={28}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 620 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-muted)' }}>
            Tema del panel
          </span>
          <SegmentedControl
            options={OPTIONS}
            value={choice}
            onChange={(v: string) => setChoice(v as 'light' | 'dark' | 'system')}
          />
        </div>

        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--body-muted)' }}>
          Elige entre el tema <strong style={{ color: 'var(--ink-muted)' }}>Claro</strong> u{' '}
          <strong style={{ color: 'var(--ink-muted)' }}>Oscuro</strong>, o deja que el panel siga la
          preferencia de tu sistema operativo con <strong style={{ color: 'var(--ink-muted)' }}>Sistema</strong>.
          La opción <strong style={{ color: 'var(--ink-muted)' }}>Sistema</strong> se ajusta en vivo cuando cambias el
          modo claro u oscuro de tu equipo, sin necesidad de recargar la página.
        </p>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--lightest-gray)',
            border: '1px solid var(--border-cool)',
            fontSize: 13,
            color: 'var(--ink-muted)',
          }}
        >
          <span style={{ color: 'var(--muted-slate)' }}>Tema aplicado actualmente:</span>
          <strong style={{ color: 'var(--ink)' }}>{RESOLVED_LABEL[resolved] ?? resolved}</strong>
          {choice === 'system' && (
            <span style={{ color: 'var(--muted-slate)' }}>(según tu sistema)</span>
          )}
        </div>
      </div>
    </Panel>
  );
}
